import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import Router from 'next/router';
import useViewportSizes from 'use-viewport-sizes';
import { connect } from 'react-redux';
import keyBy from 'lodash/keyBy';

import Title from '@components/shared/Title';
import Text from '@components/shared/Text';
import Link from '@components/shared/Link';
import Button from '@components/shared/Button';
import Slider from '@components/shared/Slider';
import AnchorTabs from '@components/shared/AnchorTabs';
import Autocomplete from '@components/shared/Autocomplete';
import AddCureOption from '@components/shared/Autocomplete/AddCureOption';
import Cure from '@components/Cure/Cure';
import SuccessStory from '@components/shared/SuccessStory';
import CureRateChoice from '@components/Conditions/CureRateChoice';
import API from '@utils/API';
import Logger from '@utils/Logger';
import Helpers from '@utils/Helpers';
import { cureCardsTypes, mdStartBreakpoint } from '@constants/common';
import { apiUrls } from '@constants/api';
import routes from '@constants/routes';
import Ga from '@utils/Ga';
import gaEvents from '@constants/ga-events';

const carouselSettings = slides => ({
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 2,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 992,
      settings: {
        infinite: true,
        slidesToShow: slides.length >= 2 ? 2 : 1
      }
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1
      }
    },
    {
      breakpoint: 575,
      settings: {
        arrows: false,
        slidesToShow: 1
      }
    }
  ]
});

const anchors = [
  { label: 'Supplements', ref: createRef() },
  { label: 'Products', ref: createRef() },
  { label: 'Recipes', ref: createRef() },
  { label: 'Success stories', ref: createRef() }
];

const storiesListSize = 20;

const ConditionDetails = ({
  typesList,
  condition: {
    id,
    file,
    slug,
    name,
    products,
    supplements,
    recipes,
    success_combinations,
    curerate_choice
  }
}) => {
  const [viewportWidth] = useViewportSizes(500);
  const typesDict = keyBy(typesList, 'entry');
  const curesConfigs = [
    {
      ...typesDict.supplement,
      title: `Best supplements for ${name}`,
      columns: { xxs: 1, xs: 2, md: 4 },
      list: supplements,
      ref: anchors[0].ref,
      cardType: cureCardsTypes.vertical
    },
    {
      ...typesDict.product,
      title: `Best products for ${name}`,
      columns: { xxs: 1, xs: 2, md: 4 },
      list: products,
      ref: anchors[1].ref,
      cardType: cureCardsTypes.vertical
    },
    {
      ...typesDict.recipe,
      title: 'Most Helpful Recipes',
      columns: { xxs: 1, xs: 1, md: 2 },
      list: recipes,
      ref: anchors[2].ref,
      cardType: cureCardsTypes.horizontal
    }
  ];

  const handleLoadOptions = async (inputText, callback) => {
    try {
      const { data } = await API.get(apiUrls.cures.search, {
        search: inputText,
        disease_condition: slug
      });

      const groupedOptions = typesList
        .map(type => ({
          label: type.title,
          options: data[type.title.toLowerCase()]
        }))
        .filter(({ options }) => Helpers.arrayHasItems(options));

      callback(groupedOptions);
    } catch (err) {
      Logger.error(err);
    }
  };

  const debouncedLoadOptions = debounce(
    (inputText, callback) => handleLoadOptions(inputText, callback),
    350
  );
  const handleSearch = inputText => {
    Ga.sendSearchEvent(gaEvents.conditionSearch, inputText);
    Router.push({
      pathname: routes.cures.search.path(slug),
      query: { search: inputText }
    });
  };

  const handleSelectOption = ({ slug }) => {
    Ga.sendSearchEvent(
      gaEvents.conditionSearch,
      routes.cures.details.path(slug)
    );
    Router.push(routes.cures.details.pattern, routes.cures.details.path(slug));
  };

  const handleScrollToSection = ({ ref }) => {
    // Offset for correct anchors scroll with mobile sticky bar, also looks good on desktop
    const stickyBarOffset = -75;
    Helpers.scrollToSection({ ref, offset: stickyBarOffset });
  };

  const searchFieldPlaceholder =
    viewportWidth < mdStartBreakpoint
      ? 'Search for something'
      : 'Search for supplements, products or recipes';

  return (
    <div className="condition">
      <div className="container">
        <div className="condition__header">
          <Title size="lg" bold={true} className="condition__title">
            Rated & Reviewed By People With {name}
          </Title>
          <Text size="lg" className="condition__subtext">
            CureRated remedies to help you manage your {name}
          </Text>

          <div className="condition__search">
            <Autocomplete
              key={id}
              onSearch={handleSearch}
              onChange={handleSelectOption}
              loadOptions={debouncedLoadOptions}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.slug}
              noOptionsMessage={() => null}
              instanceId="condition-autocomplete"
              noResultsMessage={() => (
                <AddCureOption
                  condition={{
                    id,
                    file,
                    slug,
                    name
                  }}
                />
              )}
              placeholder={searchFieldPlaceholder}
            />
          </div>

          {file && (
            <div className="condition__media">
              <img src={file} alt={name} className="condition__img" />
            </div>
          )}
        </div>
      </div>
      <AnchorTabs
        anchors={anchors}
        className="container condition__tabs"
        onClick={handleScrollToSection}
      />
      <div className="container">
        <div className="condition__content">
          <CureRateChoice condition={slug} list={curerate_choice} />

          {curesConfigs.map(config => {
            const colunmWidht = {
              xxs: 12 / config.columns.xxs,
              xs: 12 / config.columns.xs,
              md: 12 / config.columns.md
            };

            return (
              <div
                key={config.slug}
                className="condition__list"
                ref={config.ref}
              >
                <div className="condition__subheader">
                  <Title className="condition__subtitle" heading="h2" size="md">
                    {config.title}
                    <Link
                      icon="chevron-right"
                      className="condition__link"
                      href={routes.cures.list.pattern}
                      as={routes.cures.list.path(slug, config.slug)}
                    >
                      See all
                    </Link>
                  </Title>
                </div>
                <div className="condition__items">
                  <div className="row">
                    {config.list.map(item => (
                      <div
                        key={item.slug}
                        className={`col-xxs-${colunmWidht.xxs} col-xs-${colunmWidht.xs} col-md-${colunmWidht.md} condition__item`}
                      >
                        <Cure cure={item} type={config.cardType} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="condition__subfooter">
                  <Link
                    icon="chevron-right"
                    className="condition__link"
                    href={routes.cures.list.pattern}
                    as={routes.cures.list.path(slug, config.slug)}
                  >
                    See all
                  </Link>
                </div>
              </div>
            );
          })}

          <div className="condition__list" ref={anchors[3].ref}>
            <div className="condition__subheader">
              <Title className="condition__subtitle" heading="h2" size="md">
                Stories from the {name} Community
                <Link
                  icon="chevron-right"
                  className="condition__link"
                  href={routes.combinations.list.pattern}
                  as={routes.combinations.list.path(slug)}
                >
                  See all
                </Link>
              </Title>

              <Text className="condition__text">
                Let&apos;s celebrate even the small wins. Found something that
                worked? A combination of things? Here&apos;s where you share
                your success and help us uncover winning patterns.
              </Text>

              <Button
                color="yellow"
                className="condition__btn"
                href={routes.combinations.create.pattern}
                as={routes.combinations.create.path(slug)}
              >
                Add Your Own
              </Button>
            </div>
            <div className="condition__items">
              {Helpers.arrayHasItems(success_combinations) && (
                <Slider
                  {...carouselSettings(success_combinations)}
                  className="condition__slider"
                >
                  {success_combinations.map(({ created_by, ...story }) => (
                    <SuccessStory
                      key={story.id}
                      story={story}
                      author={created_by}
                    />
                  ))}

                  {success_combinations.length === storiesListSize && (
                    <SuccessStory
                      empty={{
                        button: {
                          onClick() {
                            Router.push(
                              routes.combinations.list.pattern,
                              routes.combinations.list.path(slug)
                            );
                          }
                        }
                      }}
                    />
                  )}
                </Slider>
              )}
            </div>
            <div className="condition__subfooter">
              <Link
                icon="chevron-right"
                className="condition__link"
                href={routes.combinations.list.pattern}
                as={routes.combinations.list.path(slug)}
              >
                See all
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ConditionDetails.propTypes = {
  route: PropTypes.object,
  typesList: PropTypes.array,
  condition: PropTypes.object
};

const mapStateToProps = ({ cures: { typesList } }) => ({
  typesList
});

export default connect(mapStateToProps)(ConditionDetails);
